import { BdcDocPrefix, BdcDocSequence, BdcInvoiceSummary, BdcSequenceKind } from "../../../types/bdc.type";
import {
  BDC_CANCEL_REASON_MAX,
  BDC_CREDIT_NOTE_NO_DIGITS,
  BDC_INVOICE_NO_DIGITS,
  BDC_INVOICE_NO_MAX,
} from "../constants";

/** `B` + 9 digits — 269005694 → B269005694. */
export const formatInvoiceNo = (prefix: BdcDocPrefix, nextNo: number): string =>
  formatSequenceNo(prefix, nextNo, BDC_INVOICE_NO_DIGITS);

/**
 * The Set box takes the full number (`B269005694`) or just its digits (`269005694`). Returns the
 * running number (1 … 999,999,999) or why it is refused. A prefix typed for the other series is
 * refused, so `TB…` is never Set on the real series by mistake.
 */
export const parseInvoiceNoInput = (text: string, prefix: BdcDocPrefix): { value: number } | { error: string } =>
  parseSequenceNoInput(text, { prefix, digits: BDC_INVOICE_NO_DIGITS, kind: "invoice" }, ["B", "TB"]);

// ---- every running series (P2: INV, CN, INV book) ----

type SequenceLike = Pick<BdcDocSequence, "prefix" | "kind" | "digits">;

/** What a series numbers — the API says so; a pre-CN API only has B / TB. */
export const sequenceKind = (seq: SequenceLike): BdcSequenceKind => {
  if (seq.kind) return seq.kind;
  if (/^T?B$/.test(seq.prefix)) return "invoice";
  if (/^T?C\d$/.test(seq.prefix)) return "credit_note";
  return "book";
};

/** Zero-padded width of a series; null = not padded (book numbers). */
export const sequenceDigits = (seq: SequenceLike): number | null => {
  if (seq.digits !== undefined) return seq.digits;
  switch (sequenceKind(seq)) {
    case "invoice":
      return BDC_INVOICE_NO_DIGITS;
    case "credit_note":
      return BDC_CREDIT_NOTE_NO_DIGITS;
    default:
      return null;
  }
};

/** The largest number a series holds: 9…9 of its width, or 999,999,999 unpadded. */
export const sequenceMax = (digits: number | null): number => (digits ? 10 ** digits - 1 : BDC_INVOICE_NO_MAX);

/** `C2` + 7 digits → C20042870 · a book number is the bare integer behind its code (`VY 55675`). */
export const formatSequenceNo = (prefix: string, nextNo: number, digits: number | null): string =>
  digits ? `${prefix}${String(nextNo).padStart(digits, "0")}` : `${prefix} ${nextNo}`;

export const SEQUENCE_KIND_LABEL: Record<BdcSequenceKind, string> = {
  invoice: "เลข INV",
  credit_note: "เลข CN",
  book: "เลขเล่ม INV",
};

/**
 * Set of any series: the full number (`C20042870`, `VY55675`) or its digits. A number typed with
 * another known series' prefix is refused by name.
 */
export const parseSequenceNoInput = (
  text: string,
  seq: SequenceLike,
  knownPrefixes: string[] = [],
): { value: number } | { error: string } => {
  const digits = sequenceDigits(seq);
  const prefix = seq.prefix.toUpperCase();
  const cleaned = text.replace(/[\s,]/g, "").toUpperCase();
  const example = formatSequenceNo(seq.prefix, 1, digits).replace(" ", "");
  if (!cleaned) return { error: `กรอก${SEQUENCE_KIND_LABEL[sequenceKind(seq)]} ถัดไป` };
  let number: string | null = null;
  if (/^\d+$/.test(cleaned)) number = cleaned;
  else if (cleaned.startsWith(prefix) && /^\d+$/.test(cleaned.slice(prefix.length))) number = cleaned.slice(prefix.length);
  if (number === null) {
    const other = knownPrefixes
      .map((p) => p.toUpperCase())
      .filter((p) => p !== prefix && cleaned.startsWith(p) && /^\d+$/.test(cleaned.slice(p.length)))
      .sort((a, b) => b.length - a.length)[0];
    if (other) return { error: `เลขนี้เป็นของชุด ${other} ไม่ใช่ชุด ${seq.prefix}` };
    return { error: `รูปแบบเลขไม่ถูกต้อง (เช่น ${example})` };
  }
  const width = digits ?? String(BDC_INVOICE_NO_MAX).length;
  if (number.length > width) return { error: `ตัวเลขไม่เกิน ${width} หลัก` };
  const value = Number(number);
  const max = sequenceMax(digits);
  if (value < 1 || value > max) return { error: `เลขต้องอยู่ระหว่าง 1 ถึง ${max.toLocaleString("en-US")}` };
  return { value };
};

/** ยกเลิก INV needs a reason, 1 … 500 characters after trimming. */
export const checkCancelReason = (text: string): string | null => {
  const reason = text.trim();
  if (!reason) return "กรุณาระบุเหตุผลที่ยกเลิก";
  if (reason.length > BDC_CANCEL_REASON_MAX) return `เหตุผลยาวไม่เกิน ${BDC_CANCEL_REASON_MAX} ตัวอักษร`;
  return null;
};

/** The INV a PO is on now: the newest one still issued (a PO has at most one live INV). */
export const liveInvoice = (invoices: BdcInvoiceSummary[]): BdcInvoiceSummary | null =>
  invoices
    .filter((inv) => inv.status === "issued")
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null;
